/**
 * Le contrôle du compte client : où le grand livre et les vues divergent.
 *
 * ## Ce que cet écran ne fait pas
 *
 * Il ne corrige rien. Étape 1 d'un chemin en quatre : le grand livre MESURE.
 * Chaque écart est une décision comptable à prendre — créditer, ou mettre en
 * perte — et ces décisions se prennent dans les écrans qui savent déjà les
 * écrire. Un bouton « corriger » ici écrirait sans que personne n'ait tranché.
 *
 * ## L'avertissement qui vient avant les chiffres
 *
 * Le rapprochement n'est pas terminé. La file des entrées est vide, mais des
 * centaines de virements sortants attendent encore — dont une partie que des
 * familles peuvent revendiquer. Pour celles-là, le grand livre croit encore
 * devoir un argent déjà parti en banque, et leur solde est PROVISOIRE.
 *
 * Le bandeau le dit avant tout le reste, et chaque ligne concernée le répète.
 * Sans quoi un solde provisoire se lirait comme un définitif, et une décision
 * se prendrait sur un chiffre qui doit encore bouger.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, BookOpen, ChevronDown, ChevronRight, Clock, FileText, Loader2, RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import { euros } from '../../../lib/finances/situation';
import { bilanDesEcarts, nomDeLaFamille } from '../../../lib/grandLivre/ecart';
import {
  lireLEtatDuRapprochement, lireLesEcarts, lireLeGrandLivre, lireLesFacturesACrediter,
  lireLesSortiesDeLaFamille, rafraichirLeControle,
  type Controle, type EcartDeFamille, type EtatDuRapprochement, type FactureACrediter,
  type MouvementDuLivre, type SortieEnFile,
} from '../../../lib/grandLivre/controle.supabase';
import { CHEMINS, avecRecherche } from '../../../lib/navigation/chemins';

function ControleDuGrandLivre() {
  const [ouvert, setOuvert] = useState(false);
  const [controle, setControle] = useState<Controle | null>(null);
  const [etat, setEtat] = useState<EtatDuRapprochement | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const [recalcul, setRecalcul] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [c, e] = await Promise.all([lireLesEcarts(), lireLEtatDuRapprochement()]);
      setControle(c);
      setEtat(e);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setChargement(false);
    }
  }, []);

  /*
    Recalculer et relire sont deux gestes, et l'écran ne les confond pas.
    Ouvrir le panneau LIT l'instantané — 1 ms. Le bouton le RECALCULE — une
    demi-seconde — et c'est une demande explicite, parce qu'une liste
    d'anomalies qui bouge pendant qu'on la traite est pire qu'une liste qui
    date de ce matin.
  */
  const recalculer = useCallback(async () => {
    setRecalcul(true);
    setErreur(null);
    try {
      await rafraichirLeControle();
      await charger();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setRecalcul(false);
    }
  }, [charger]);

  useEffect(() => {
    if (ouvert && !controle && !chargement) void charger();
  }, [ouvert, controle, chargement, charger]);

  const bilan = controle ? bilanDesEcarts(controle.ecarts.map((e) => e.lecture)) : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => setOuvert((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
      >
        <span className="flex items-center gap-2">
          {ouvert ? <ChevronDown className="h-4 w-4 text-gray-400" />
                  : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <BookOpen className="h-4 w-4 text-indigo-600" />
          <span>
            <span className="font-medium text-gray-800">Contrôle du compte client</span>
            <span className="block text-sm text-gray-600">
              Le solde d'une famille, lu comme la somme de ses mouvements, et là où il
              ne dit pas la même chose que la situation ci-dessus.{' '}
              {/* Demandé le 20 septembre : « devais-je sélectionner un compte ? ».
                  Non — et un écran qui ne le dit pas laisse attendre devant un
                  chargement en croyant avoir oublié un filtre. */}
              <span className="text-gray-500">
                Aucun compte à choisir : les 2 419 familles sont passées en revue, et
                seules celles qui divergent s'affichent.
              </span>
            </span>
          </span>
        </span>
        {bilan && bilan.familles > 0 && (
          <span className="shrink-0 text-sm font-medium text-amber-800 bg-amber-100 rounded-full px-3 py-1 tabular-nums">
            {bilan.familles} {bilan.familles > 1 ? 'familles' : 'famille'}
          </span>
        )}
      </button>

      {ouvert && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          {chargement && (
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Lecture du grand livre…
            </p>
          )}

          {erreur && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              {erreur}
            </p>
          )}

          {/* --- Ce qui n'est pas encore encodé, avant tout le reste ----- */}
          {etat?.enCours && <RapprochementEnCours etat={etat} />}

          {bilan && controle && (
            <>
              <Bilan
                bilan={bilan}
                total={controle.total}
                calculeLe={controle.calculeLe}
                onRecalculer={() => void recalculer()}
                recalcul={recalcul}
              />
              <div className="space-y-3">
                {controle.ecarts.map((e) => (
                  <LigneDEcart key={e.faits.userId} ecart={e} />
                ))}
              </div>
              {controle.ecarts.length === 0 && (
                <p className="text-sm text-gray-600">
                  Aucun écart : le grand livre et la situation financière disent la
                  même chose sur toutes les familles.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Le bandeau qui empêche de lire un provisoire comme un définitif.
 *
 * Il ne se contente pas d'annoncer un nombre : il dit ce que ce nombre CHANGE,
 * c'est-à-dire que les soldes des familles concernées vont encore bouger, et
 * qu'aucune décision ne devrait se prendre sur eux d'ici là.
 */
function RapprochementEnCours({ etat }: { etat: EtatDuRapprochement }) {
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
      <p className="font-medium text-amber-900 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        Le rapprochement n'est pas terminé : ces soldes sont provisoires
      </p>
      <div className="text-sm text-amber-900 mt-2 space-y-2">
        {etat.entreesEnFile > 0 ? (
          <p>
            <strong>{etat.entreesEnFile}</strong> virement
            {etat.entreesEnFile > 1 ? 's' : ''} entrant
            {etat.entreesEnFile > 1 ? 's attendent' : ' attend'} encore d'être placé
            {etat.entreesEnFile > 1 ? 's' : ''}.
          </p>
        ) : (
          <p>Les entrées sont toutes encodées.</p>
        )}
        <p>
          <strong>{etat.sortiesEnFile}</strong> virement
          {etat.sortiesEnFile > 1 ? 's' : ''} sortant
          {etat.sortiesEnFile > 1 ? 's' : ''} en revanche
          {etat.sortiesEnFile > 1 ? ' attendent' : ' attend'} encore, dont{' '}
          <strong>{etat.sortiesRattachees}</strong> qu'une famille peut revendiquer —{' '}
          {euros(etat.sortiesRattacheesCents)} sur{' '}
          <strong>{etat.famillesProvisoires}</strong> famille
          {etat.famillesProvisoires > 1 ? 's' : ''}.
        </p>
        <p>
          Pour ces familles, le grand livre croit encore devoir un argent{' '}
          <strong>déjà parti en banque</strong>. Leur solde bougera quand ces virements
          seront imputés : rien ne se décide sur ces chiffres-là avant.
        </p>
      </div>
    </div>
  );
}

function Bilan({
  bilan, total, calculeLe, onRecalculer, recalcul,
}: {
  bilan: ReturnType<typeof bilanDesEcarts>;
  total: number;
  calculeLe: string | null;
  onRecalculer: () => void;
  recalcul: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-gray-700">
            <strong className="tabular-nums">{total}</strong> famille
            {total > 1 ? 's' : ''} sur lesquelles les deux lectures divergent, pour{' '}
            <strong className="tabular-nums">{euros(bilan.totalAbsoluCents)}</strong>{' '}
            en valeur absolue.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            En valeur absolue, parce que deux écarts de signes opposés ne s'annulent
            pas : ce sont deux dossiers à traiter, pas zéro.
          </p>
        </div>
        <button
          onClick={onRecalculer}
          disabled={recalcul}
          className="btn-outline flex items-center gap-2 shrink-0 text-sm"
        >
          <RefreshCw className={clsx('h-4 w-4', recalcul && 'animate-spin')} />
          Recalculer
        </button>
      </div>

      {/* L'âge du calcul, avec les chiffres qu'il qualifie. Un contrôle qui ne
          dit pas sa date se lit comme s'il venait d'être fait. */}
      <p className="text-xs text-gray-500 mt-2">
        {calculeLe
          ? `Calculé le ${new Date(calculeLe).toLocaleString('fr-BE')}. Le recalcul prend environ une seconde.`
          : "Jamais calculé. Utilisez « Recalculer »."}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
        {bilan.parMotif.map((m) => (
          <div key={m.code} className="bg-white rounded-lg border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-800">{m.motif.label}</p>
            <p className="text-sm text-gray-600 tabular-nums mt-0.5">
              {euros(m.montantCents)} · {m.familles} famille{m.familles > 1 ? 's' : ''}
            </p>
            {m.motif.decision && (
              <p className="text-xs text-gray-500 mt-1">{m.motif.decision}</p>
            )}
          </div>
        ))}
      </div>

      {bilan.famillesInexpliquees > 0 && (
        <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
          <strong>{bilan.famillesInexpliquees}</strong> famille
          {bilan.famillesInexpliquees > 1 ? 's' : ''} dont l'écart n'est pas
          entièrement expliqué — {euros(bilan.resteInexpliqueCents)}. Un motif
          existe que personne n'a encore nommé.
        </p>
      )}
    </div>
  );
}

function LigneDEcart({ ecart }: { ecart: EcartDeFamille }) {
  const { faits, lecture } = ecart;
  const [ouvert, setOuvert] = useState(false);
  const [livre, setLivre] = useState<MouvementDuLivre[] | null>(null);
  const [sorties, setSorties] = useState<SortieEnFile[] | null>(null);
  const [aCrediter, setACrediter] = useState<FactureACrediter[] | null>(null);
  const [chargement, setChargement] = useState(false);

  const aCrediterAttendu = lecture.motifs.some(
    (m) => m.code === 'facture_annulee_non_creditee',
  );

  useEffect(() => {
    if (!ouvert || livre || chargement) return;
    setChargement(true);
    void Promise.all([
      lireLeGrandLivre(faits.userId),
      lecture.provisoire ? lireLesSortiesDeLaFamille(faits.userId) : Promise.resolve([]),
      aCrediterAttendu ? lireLesFacturesACrediter(faits.userId) : Promise.resolve([]),
    ])
      .then(([m, s, f]) => { setLivre(m); setSorties(s); setACrediter(f); })
      .catch(() => { setLivre([]); setSorties([]); setACrediter([]); })
      .finally(() => setChargement(false));
  }, [ouvert, livre, chargement, faits.userId, lecture.provisoire, aCrediterAttendu]);

  return (
    <div className="rounded-xl border border-gray-200">
      <button
        onClick={() => setOuvert((o) => !o)}
        className="w-full flex items-start justify-between gap-4 p-4 text-left"
      >
        <div className="min-w-0">
          <p className="font-medium text-gray-900 flex items-center gap-2">
            {ouvert ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                    : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
            {nomDeLaFamille(faits)}
            {lecture.provisoire && (
              <span className="inline-flex items-center gap-1 text-xs font-normal text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
                <Clock className="h-3 w-3" />
                provisoire
              </span>
            )}
          </p>
          <p className="text-sm text-gray-600 mt-1">{lecture.phrase}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold tabular-nums text-gray-900">
            {euros(Math.abs(faits.ecartCents))}
          </p>
          <p className="text-xs text-gray-500 tabular-nums">
            livre {euros(faits.soldeCents)} · vues {euros(faits.soldeDesVuesCents)}
          </p>
        </div>
      </button>

      {ouvert && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-3">
          <div className="space-y-2">
            {lecture.motifs.map((m) => (
              <div key={m.code} className="text-sm">
                <p className="font-medium text-gray-800 tabular-nums">
                  {m.motif.label} · {euros(m.montantCents)}
                </p>
                <p className="text-gray-600">{m.motif.explication}</p>
                {m.motif.decision && (
                  <p className="text-gray-500 italic">{m.motif.decision}</p>
                )}
              </div>
            ))}
            {lecture.resteInexpliqueCents !== 0 && (
              <p className="text-sm text-red-800">
                {euros(Math.abs(lecture.resteInexpliqueCents))} qu'aucun motif connu
                n'explique.
              </p>
            )}
          </div>

          {chargement && (
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Lecture des mouvements…
            </p>
          )}

          {aCrediter && aCrediter.length > 0 && <ACrediter factures={aCrediter} />}

          {sorties && sorties.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-900">
                Parti en banque, pas encore imputé
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-900">
                {sorties.map((s) => (
                  <li key={s.refundId} className="flex justify-between gap-3">
                    <span className="truncate">
                      {s.date ?? '—'} · {s.communication ?? 'sans communication'}
                      <span className="text-amber-700"> ({s.rattachePar})</span>
                    </span>
                    <span className="tabular-nums shrink-0">{euros(s.montantCents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {livre && livre.length > 0 && <Livre mouvements={livre} solde={faits.soldeCents} />}
        </div>
      )}
    </div>
  );
}

/**
 * Les factures à créditer, nommées, avec le lien qui mène là où on les crédite.
 *
 * Ce panneau n'émet rien. Une note de crédit est une pièce NUMÉROTÉE : elle
 * s'émet là où l'écran sait déjà le faire, avec son formulaire, son motif et
 * son PDF — pas depuis un tableau de contrôle où un clic de trop en produirait
 * une qu'il faudrait ensuite corriger par une autre.
 *
 * Le montant affiché est ce qui RESTE à créditer, jamais le montant de la
 * facture. Sur CDV-CSSA-2500539, 95,00 € facturés, 50,00 € déjà crédités : une
 * note de 95,00 € ferait passer cette famille du premier motif d'écart au
 * troisième.
 */
function ACrediter({ factures }: { factures: FactureACrediter[] }) {
  const total = factures.reduce((t, f) => t + f.resteACrediterCents, 0);
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
      <p className="text-sm font-medium text-indigo-900">
        À créditer : {euros(total)} sur {factures.length} facture
        {factures.length > 1 ? 's' : ''} annulée{factures.length > 1 ? 's' : ''}
      </p>
      <ul className="mt-2 space-y-1 text-sm text-indigo-900">
        {factures.map((f) => (
          <li key={f.invoiceId} className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="flex items-baseline gap-2">
              <Link
                to={avecRecherche(CHEMINS.comptabilite.factures, f.invoiceNumber)}
                className="inline-flex items-center gap-1 font-mono text-xs underline decoration-dotted underline-offset-2 hover:decoration-solid"
              >
                <FileText className="h-3.5 w-3.5" />
                {f.invoiceNumber}
              </Link>
              <span className="text-indigo-700 tabular-nums">
                {euros(f.montantCents)} facturés
                {f.dejaCrediteCents > 0 && `, ${euros(f.dejaCrediteCents)} déjà crédités`}
              </span>
            </span>
            <span className="font-medium tabular-nums">{euros(f.resteACrediterCents)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-indigo-800">
        Le lien ouvre l'écran des factures sur ce numéro, où la note de crédit s'émet.
      </p>
    </div>
  );
}

const NATURES: Record<MouvementDuLivre['nature'], string> = {
  facture: 'Facture',
  note_de_credit: 'Note de crédit',
  encaissement: 'Encaissement',
  remboursement: 'Remboursement',
};

/** Le compte client, ligne à ligne, avec le solde qui court. */
function Livre({ mouvements, solde }: { mouvements: MouvementDuLivre[]; solde: number }) {
  let courant = 0;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-gray-500 text-left">
            <th className="py-1 pr-3 font-medium">Date</th>
            <th className="py-1 pr-3 font-medium">Mouvement</th>
            <th className="py-1 pr-3 font-medium">Pièce</th>
            <th className="py-1 pr-3 font-medium text-right">Débit</th>
            <th className="py-1 pr-3 font-medium text-right">Crédit</th>
            <th className="py-1 font-medium text-right">Solde</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {mouvements.map((m) => {
            courant += m.signeCents;
            return (
              <tr key={`${m.ligneId}-${m.nature}`} className="text-gray-700">
                <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{m.date ?? '—'}</td>
                <td className="py-1.5 pr-3">
                  {NATURES[m.nature]}
                  {m.imputation && (
                    <span className="text-gray-400"> · {m.imputation.replace('_', ' ')}</span>
                  )}
                </td>
                <td className="py-1.5 pr-3 truncate max-w-[16rem]">{m.pieceNumero ?? '—'}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {m.sens === 'debit' ? euros(m.montantCents) : ''}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {m.sens === 'credit' ? euros(m.montantCents) : ''}
                </td>
                <td className="py-1.5 text-right tabular-nums text-gray-900">
                  {euros(courant)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-300 font-medium text-gray-900">
            <td className="py-2" colSpan={5}>
              Solde du compte client
            </td>
            <td className="py-2 text-right tabular-nums">{euros(solde)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default ControleDuGrandLivre;
